// Terrain.ts
import { Actor, CollisionType, Color, Engine, GraphicsGroup, Line, Shape, Vector, lerp, vec } from 'excalibur';
import { clamp01 } from './clamp01';
import { dedupeMonotonicX } from './dedupeMonotonicX';
import { mulberry32 } from './mulberry32';
import { TerrainOptions } from './TerrainOptions';
import { TerrainSegment } from './TerrainSegment';

export class Terrain extends Actor {
    private rng!: () => number;
    private segments: TerrainSegment[] = [];

    constructor(private options: TerrainOptions = {}) {
        super({ pos: vec(0, 0), anchor: Vector.Zero });
    }

    public override onInitialize(engine: Engine): void {
        this.body.collisionType = CollisionType.Fixed;
        this.graphics.anchor = Vector.Zero;

        const seed = (this.options.seed ?? Date.now() & 0xffffffff) >>> 0;
        this.rng = mulberry32(seed);

        this.generate(engine);
        this.applyColliders();
        this.applyGraphics();
    }

    private generate(engine: Engine): void {
        const width = this.options.width ?? engine.drawWidth;
        const height = this.options.height ?? engine.drawHeight;

        const levelFractions = this.options.levelFractions ?? [0.68, 0.8, 0.9];
        const levelsY: [number, number, number] =
            this.options.levelsY ?? (levelFractions.map((f) => clamp01(f) * height) as [number, number, number]);

        const xStep = Math.max(4, this.options.xStep ?? 24);
        const noiseAmp = Math.max(0, this.options.noiseAmp ?? 18);
        const smoothing = clamp01(this.options.noiseSmoothing ?? 0.75);

        const marginLeft = this.options.marginLeft ?? 0;
        const marginRight = this.options.marginRight ?? 0;
        const usableWidth = Math.max(0, width - marginLeft - marginRight);

        this.segments = [];

        const pts = this.generateBandPolyline(0, usableWidth, levelsY[0], xStep, noiseAmp, smoothing);

        for (let p = 0; p < pts.length - 1; p++) {
            const a = pts[p];
            const b = pts[p + 1];
            if (a.x !== b.x || a.y !== b.y) {
                this.segments.push({ a, b });
            }
        }
    }

    private generateBandPolyline(x0: number, x1: number, baseY: number, xStep: number, noiseAmp: number, smoothing: number): Vector[] {
        const pts: Vector[] = [];
        let x = x0;
        let y = baseY;

        // Start with a random offset
        let target = baseY + (this.rng() * 2 - 1) * noiseAmp;
        y = lerp(y, target, 1 - smoothing);
        pts.push(vec(x, y));

        while (x < x1) {
            x = Math.min(x1, x + xStep);

            // Random-walk target, then smooth towards it
            target = baseY + (this.rng() * 2 - 1) * noiseAmp;
            y = lerp(y, target, 1 - smoothing);

            pts.push(vec(x, y));
        }

        return dedupeMonotonicX(pts);
    }

    private applyColliders(): void {
        const edges = this.segments.map((s) => Shape.Edge(s.a, s.b));
        this.collider.useCompositeCollider(edges);
    }

    private applyGraphics(): void {
        const color = this.options.color ?? Color.White;
        const thickness = this.options.thickness ?? 2;

        const members = this.segments.map(
            (s) =>
                new Line({
                    start: s.a,
                    end: s.b,
                    color,
                    thickness,
                }),
        );

        this.graphics.use(
            new GraphicsGroup({
                useAnchor: false,
                members,
            }),
        );
    }
}
